import { useEffect, useState } from "react";
import styles from "./HoverWord.module.scss";
import { BIG_H, useWordBox, type OverlayProps } from "./shared";

type Phase = "select" | "clear" | "type" | "done";

const SELECT_MS = 190;
const CLEAR_MS = 150;
const CHAR_MS = 17; /* editor typing speed */
const DWELL_MS = 520; /* the suggest widget stays up long enough to read */

type Tok = [string, "kw" | "fn" | "var" | "num" | "pn"];

/* A condensed excerpt of the trail pass in ReactiveField.tsx, tokenized
   with GLSL's TextMate scopes as Dark / Light Modern colour them — the
   editor writes the code that actually runs behind this page. */
const LINES: Tok[][] = [
  [
    ["float", "kw"],
    [" ", "pn"],
    ["d", "var"],
    [" = ", "pn"],
    ["length", "fn"],
    ["(", "pn"],
    ["p", "var"],
    [");", "pn"],
  ],
  [
    ["v", "var"],
    [" += ", "pn"],
    ["k", "var"],
    [" * ", "pn"],
    ["exp", "fn"],
    ["(-", "pn"],
    ["d", "var"],
    [" * ", "pn"],
    ["d", "var"],
    [");", "pn"],
  ],
  [
    ["frag", "var"],
    [" = ", "pn"],
    ["vec4", "kw"],
    ["(", "pn"],
    ["v", "var"],
    [");", "pn"],
  ],
];
/* Statement-size words are 64-70px wide: three lines there set at 7px
   and stack taller than the line. The miniature types two short lines of
   the same falloff (squared distance, then its Gaussian), a pair that sits
   inside the text's own band and clears the punctuation after the word,
   and drops the gutter and IntelliSense with it. */
const LINES_SM: Tok[][] = [
  [
    ["d", "var"],
    [" = ", "pn"],
    ["dot", "fn"],
    ["(p,p);", "pn"],
  ],
  [
    ["v", "var"],
    [" = ", "pn"],
    ["exp", "fn"],
    ["(-d);", "pn"],
  ],
];

const lineLength = (l: Tok[]) => l.reduce((n, [t]) => n + t.length, 0);

/* IntelliSense: typing the "e" of exp on line 2 opens the suggest widget,
   "x" narrows it, a beat to read, then Tab accepts and inserts the "p". */
const SUG_LINE = 1;
const SUG_COL = 9; /* "v += k * " */
const SUG_ITEMS: Array<[string, string]> = [
  ["exp", "float exp(float x)"],
  ["exp2", "float exp2(float x)"],
];

/* Absolute char index → reveal time (ms after typing starts), plus the
   window the suggest widget is open. */
function schedule(lines: Tok[][], suggest: boolean) {
  const total = lines.reduce((n, l) => n + lineLength(l), 0);
  const sugAt = lineLength(lines[0]) + SUG_COL; /* index of the "e" */
  const times: number[] = [];
  let t = 0;
  for (let i = 0; i < total; i++) {
    t += CHAR_MS;
    if (suggest && i === sugAt + 2) t += DWELL_MS - CHAR_MS; /* Tab */
    times.push(t);
  }
  return {
    total,
    times,
    sugAt,
    open: suggest ? times[sugAt] : Infinity,
    accept: suggest ? times[sugAt + 2] : Infinity,
  };
}

/* Menlo (VS Code's default) advances 0.602em; the gutter is ~2.6ch. */
const ADVANCE = 0.602;
const GUTTER = 2.6;
const MIN_FULL_FS = 8;

/* Full editor only when the line box is display-size AND the full lines
   still set at ≥8px in the word's width — otherwise the miniature. */
function isSmall(w: number, h: number) {
  if (h < BIG_H) return true;
  const longest = Math.max(...LINES.map(lineLength));
  return w / ((longest + GUTTER) * ADVANCE) < MIN_FULL_FS;
}

/* Slice a tokenized line to its first `count` characters. */
function sliceLine(line: Tok[], count: number): Tok[] {
  const out: Tok[] = [];
  let left = count;
  for (const [t, c] of line) {
    if (left <= 0) break;
    out.push([t.slice(0, left), c]);
    left -= t.length;
  }
  return out;
}

const renderToks = (line: Tok[]) =>
  line.map(([t, c], j) => (
    <span key={j} className={styles[`vs_${c}`]}>
      {t}
    </span>
  ));

/* codicon symbol-method: the purple cube IntelliSense puts on functions */
function MethodIcon({ size }: { size: number }) {
  return (
    <svg
      className={styles.vsMethod}
      width={size}
      height={size}
      viewBox="0 0 16 16"
    >
      <path d="M13.51 4l-5-3h-1l-5 3-.49.86v6l.49.85 5 3h1l5-3 .49-.85v-6L13.51 4zm-6 9.56l-4.5-2.7V5.7l4.5 2.45v5.41zM3.27 4.7l4.74-2.84 4.74 2.84-4.74 2.59L3.27 4.7zm9.74 6.16l-4.5 2.7V8.15l4.5-2.45v5.16z" />
    </svg>
  );
}

/* VS Code quotation. The word is selected in VS Code's selection blue and
   deleted; then the editor types the site's shader where it stood, in
   VS Code's own font and Dark / Light Modern syntax colours, with the
   line-number gutter, the current-line outline and the thin caret. On
   line 2 IntelliSense opens on "ex", holds a beat, and Tab accepts exp.
   The buffer centres on the line's cap axis, so it sits on the same
   level as the words around it. Small words type a two-line miniature
   without the gutter or the suggest widget. The host's data-phase hides
   the original glyphs once the deletion lands; the word box keeps its
   measured width, so nothing reflows. */
export function CodeOverlay({ hostRef, hot, text }: OverlayProps) {
  const box = useWordBox(hostRef);
  const [phase, setPhase] = useState<Phase>("select");
  const [chars, setChars] = useState(0);
  const [suggest, setSuggest] = useState(false);

  /* The phase lives on the host so the word's own glyphs can react. The
     exit clears it at once: the word fades back in under the closing
     editor instead of after it. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!hot) {
      delete host.dataset.phase;
      return;
    }
    host.dataset.phase = phase;
    return () => {
      delete host.dataset.phase;
    };
  }, [hostRef, phase, hot]);

  useEffect(() => {
    /* The word box is measurable synchronously on mount — the schedule
       must not wait for useWordBox's state. */
    const r = hostRef.current?.getBoundingClientRect();
    const small = isSmall(r?.width ?? 0, r?.height ?? 0);
    const s = schedule(small ? LINES_SM : LINES, !small);
    const timers: number[] = [];
    let raf = 0;
    timers.push(window.setTimeout(() => setPhase("clear"), SELECT_MS));
    timers.push(
      window.setTimeout(() => {
        setPhase("type");
        const t0 = performance.now();
        let n = 0;
        const tick = (now: number) => {
          const el = now - t0;
          while (n < s.total && s.times[n] <= el) n++;
          setChars(n);
          setSuggest(el >= s.open && el < s.accept);
          if (n < s.total) raf = requestAnimationFrame(tick);
          else setPhase("done");
        };
        raf = requestAnimationFrame(tick);
      }, SELECT_MS + CLEAR_MS),
    );
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      cancelAnimationFrame(raf);
    };
  }, [hostRef, text]);

  if (!box) return null;
  const { w, h, axis } = box;
  const small = isSmall(w, h);
  const lines = small ? LINES_SM : LINES;
  const lengths = lines.map(lineLength);
  const maxLen = Math.max(...lengths);
  /* the miniature keeps a caret's width of air before the punctuation */
  const gutter = small ? 1 : GUTTER;
  const fit = Math.floor(w / ((maxLen + gutter) * ADVANCE));
  /* Loose leading on purpose: the buffer should breathe, never read as
     crammed into the line. */
  const lead = small ? 1.3 : 1.5;
  const fs = small
    ? Math.max(7, Math.min(13, fit))
    : Math.max(9, Math.min(14, Math.floor(h / lines.length / lead), fit));
  const lh = Math.round(fs * lead);
  const editorTop = axis - (lines.length * lh) / 2;

  /* Which line is being written, and how far into it. */
  let activeLine = 0;
  let remaining = chars;
  while (activeLine < lines.length - 1 && remaining >= lengths[activeLine]) {
    remaining -= lengths[activeLine];
    activeLine++;
  }

  /* The widget's labels align with the start of the word being completed
     (VS Code offsets the box by the icon column). */
  const col = fs * ADVANCE;
  const typed = chars - (lengths[0] + SUG_COL);

  return (
    <span className={styles.overlay} data-out={hot ? "0" : "1"} aria-hidden>
      <span className={styles.selBand} />
      {phase !== "select" && (
        <span
          className={styles.editor}
          data-done={phase === "done" ? "1" : "0"}
          style={{
            top: editorTop,
            /* the miniature centres in the word's slot, even air both sides */
            left: small ? (w - maxLen * fs * ADVANCE) / 2 : 0,
            fontSize: fs,
            lineHeight: `${lh}px`,
          }}
        >
          {!small && (
            <span
              className={styles.editorActive}
              style={{ top: activeLine * lh, height: lh }}
            />
          )}
          {lines.map((line, i) => (
            <span key={i} className={styles.editorLine}>
              {!small && (
                <span
                  className={styles.editorNum}
                  data-on={i === activeLine ? "1" : "0"}
                >
                  {i + 1}
                </span>
              )}
              {i < activeLine
                ? renderToks(line)
                : i === activeLine
                  ? renderToks(sliceLine(line, remaining))
                  : null}
              {i === activeLine && <span className={styles.editorCaret} />}
            </span>
          ))}
          {suggest && (
            <span
              className={styles.suggest}
              style={{
                left: (gutter + SUG_COL) * col - fs * 1.3,
                top: (SUG_LINE + 1) * lh + 1,
                minWidth: 25 * col,
              }}
            >
              {SUG_ITEMS.map(([label, detail], j) => (
                <span
                  key={label}
                  className={styles.suggestRow}
                  data-on={j === 0 ? "1" : "0"}
                  style={{ height: lh + 2 }}
                >
                  <MethodIcon size={Math.round(fs * 1.05)} />
                  <span>
                    <span className={styles.suggestMatch}>
                      {label.slice(0, typed)}
                    </span>
                    {label.slice(typed)}
                  </span>
                  {j === 0 && (
                    <span className={styles.suggestDetail}>{detail}</span>
                  )}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
